import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Loader2, User, UserX, Building2, ArrowRight, Search, CheckCircle2,
  AlertTriangle, Users, Percent,
} from 'lucide-react';
import { accountingAPI, agencyAPI } from '@/lib/api';
import { toast } from 'sonner';

const STATUS_STYLE = {
  matched:          { label: 'Matched',          cls: 'bg-emerald-100 text-emerald-800',    icon: CheckCircle2 },
  placeholder:      { label: 'Placeholder',      cls: 'bg-blue-100 text-blue-800',          icon: UserX },
  manual_recipient: { label: 'Manual Recipient', cls: 'bg-violet-100 text-violet-800',      icon: Users },
  needs_review:     { label: 'Needs Review',     cls: 'bg-amber-100 text-amber-800',        icon: AlertTriangle },
  unreconciled:     { label: 'Unreconciled',     cls: 'bg-slate-200 text-slate-600',        icon: AlertTriangle },
};

const FILTERS = [
  { key: 'all',              label: 'All lines',       filter: null },
  { key: 'matched',          label: 'Matched',         filter: 'matched' },
  { key: 'placeholder',      label: 'Placeholders',    filter: 'placeholder' },
  { key: 'manual_recipient', label: 'Manual Recipient',filter: 'manual_recipient' },
  { key: 'needs_review',     label: 'Needs Review',    filter: 'needs_review' },
];

/**
 * Phase 25.2 — Reconciliation Panel.
 *
 * Inline table inside CarrierImportDetailPage that becomes the operational
 * workspace once mapping is Ready. Displays every commission line together
 * with its Agent match, suggested recipient, and Amount To Pay. Supports:
 *   • Filtering by reconciliation_status
 *   • Bulk-assigning a recipient to multiple lines
 *   • Inline recipient (Agency/Subagency) picker per line
 *   • Inline Amount To Pay editor per line
 */
export default function ReconciliationPanel({ importDetail, onImportRefresh }) {
  const importId = importDetail.import_id;
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [filter, setFilter] = useState('all');
  const [agencies, setAgencies] = useState([]);
  const [summary, setSummary] = useState(importDetail.reconciliation || null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [amountDrafts, setAmountDrafts] = useState({});   // line_id → text
  const [pctDrafts, setPctDrafts] = useState({});         // line_id → text (27.53)
  const [bulkPctInput, setBulkPctInput] = useState('');    // shared % for bulk apply
  const [bulkPopoverOpen, setBulkPopoverOpen] = useState(false);
  const [bulkSearch, setBulkSearch] = useState('');
  const [savingLine, setSavingLine] = useState(null);
  const [justUpdatedIds, setJustUpdatedIds] = useState(new Set());  // flash-highlight targets

  const isReady = importDetail.status === 'ready' || importDetail.status === 'reconciled';
  const anyReconciled = (summary?.reconciled_total || 0) + (summary?.needs_review || 0) > 0;

  const loadAgencies = useCallback(async () => {
    try {
      const r = await agencyAPI.list();
      const arr = Array.isArray(r.data) ? r.data : (r.data.items || []);
      // Sort: parent-agencies first (depth ascending), then by name
      arr.sort((a, b) =>
        (a.depth ?? 0) - (b.depth ?? 0) || (a.agency_name || '').localeCompare(b.agency_name || '')
      );
      setAgencies(arr);
    } catch (e) {
      console.error('agency list failed', e);
    }
  }, []);

  const loadLines = useCallback(async ({ preserveSelection = false } = {}) => {
    setLoading(true);
    try {
      const params = { page_size: 1000 };
      const f = FILTERS.find((x) => x.key === filter)?.filter;
      if (f) params.reconciliation_status = f;
      const [linesRes, sumRes] = await Promise.all([
        accountingAPI.listLines(importId, params),
        accountingAPI.reconciliationSummary(importId).catch(() => ({ data: null })),
      ]);
      setLines(linesRes.data.items || []);
      if (sumRes.data) setSummary(sumRes.data);
      if (!preserveSelection) setSelectedIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reconciliation lines');
    } finally { setLoading(false); }
  }, [importId, filter]);

  // Lightweight — pulls JUST the counters. Called after per-row mutations so
  // the stat strip stays accurate without disturbing scroll / row order.
  const refreshSummary = useCallback(async () => {
    try {
      const { data } = await accountingAPI.reconciliationSummary(importId);
      setSummary(data);
    } catch {/* ignore — stats will resync on next full load */}
  }, [importId]);

  // Surgically patch a single line in local state. NO full refetch → the
  // viewport scroll position, active filter and pagination all stay put.
  const patchLine = useCallback((lineId, patch) => {
    setLines((prev) => prev.map((l) => (l.line_id === lineId ? { ...l, ...patch } : l)));
    // Trigger the fade-highlight
    setJustUpdatedIds((prev) => {
      const next = new Set(prev);
      next.add(lineId);
      return next;
    });
    // Clear the highlight after ~1s so the row returns to its resting state
    setTimeout(() => {
      setJustUpdatedIds((prev) => {
        const next = new Set(prev);
        next.delete(lineId);
        return next;
      });
    }, 1200);
  }, []);

  useEffect(() => { loadAgencies(); }, [loadAgencies]);
  useEffect(() => { loadLines(); }, [loadLines]);

  const startReconciliation = async () => {
    setReconciling(true);
    try {
      const r = await accountingAPI.reconcile(importId);
      toast.success(
        `Reconciled ${r.data.matched} matched · ${r.data.placeholders} placeholder(s) · ${r.data.needs_review} need review`,
      );
      // This IS a global operation — every row's status may change. Full refetch is warranted.
      await loadLines();
      onImportRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconciliation failed');
    } finally { setReconciling(false); }
  };

  const setRecipient = async (lineId, agencyId) => {
    setSavingLine(lineId);
    try {
      const { data } = await accountingAPI.setLineRecipient(
        importId, lineId, { agency_id: agencyId || null, confirm: true },
      );
      // The endpoint returns the fresh row — surgical patch, no scroll jump.
      if (data?.line) patchLine(lineId, data.line);
      refreshSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update recipient');
    } finally { setSavingLine(null); }
  };

  const commitAmount = async (lineId, rawValue) => {
    const raw = (rawValue ?? '').toString().trim();
    let payload = null;
    if (raw !== '') {
      const parsed = parseFloat(raw);
      if (Number.isNaN(parsed)) {
        toast.error('Amount To Pay must be a number');
        return;
      }
      payload = parsed;
    }
    setSavingLine(lineId);
    try {
      const { data } = await accountingAPI.setLineAmountToPay(importId, lineId, payload);
      if (data?.line) patchLine(lineId, data.line);
      refreshSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save amount');
    } finally { setSavingLine(null); }
  };

  // Phase 27.53 — Commission % path. Percent lands on the line, server
  // recomputes amount_to_pay from amount_received_by_kr × %. Clearing
  // the input (empty string) blanks BOTH fields.
  const commitPct = async (lineId, rawValue) => {
    const raw = (rawValue ?? '').toString().trim();
    let pct = null;
    if (raw !== '') {
      const parsed = parseFloat(raw);
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
        toast.error('Commission % must be a number between 0 and 100');
        return;
      }
      pct = parsed;
    }
    setSavingLine(lineId);
    try {
      const { data } = await accountingAPI.setLineCommissionPct(importId, lineId, pct);
      if (data?.line) patchLine(lineId, data.line);
      refreshSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save %');
    } finally { setSavingLine(null); }
  };

  const bulkApplyPct = async () => {
    if (selectedIds.size === 0) return;
    const raw = bulkPctInput.trim();
    if (raw === '') { toast.error('Enter a % first (0–100)'); return; }
    const pct = parseFloat(raw);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('% must be between 0 and 100'); return;
    }
    const ids = Array.from(selectedIds);
    try {
      const { data } = await accountingAPI.bulkSetCommissionPct(importId, {
        line_ids: ids, commission_pct: pct,
      });
      toast.success(
        data.skipped
          ? `Applied ${pct}% to ${data.updated} lines · ${data.skipped} skipped (no Amt Received)`
          : `Applied ${pct}% to ${data.updated} lines`,
      );
      // Optimistic patch — recompute amount_to_pay locally so the table
      // reflects the change without a full refetch.
      setLines((prev) => prev.map((l) => {
        if (!ids.includes(l.line_id)) return l;
        if (l.amount_received_by_kr == null) return l;
        return {
          ...l,
          commission_pct: pct,
          amount_to_pay: Math.round(Number(l.amount_received_by_kr) * pct) / 100,
        };
      }));
      setJustUpdatedIds(new Set(ids));
      setTimeout(() => setJustUpdatedIds(new Set()), 1200);
      setBulkPctInput('');
      refreshSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk % apply failed');
    }
  };

  const bulkAssign = async (agencyId) => {
    if (selectedIds.size === 0) return;
    setBulkPopoverOpen(false);
    const ids = Array.from(selectedIds);
    try {
      const { data } = await accountingAPI.bulkSetRecipient(importId, {
        line_ids: ids,
        agency_id: agencyId,
        confirm: true,
      });
      toast.success(`Updated ${data.updated} lines`);
      // Optimistic patch — no full refetch. We know exactly which lines got the new agency.
      const ag = agencies.find((a) => a.agency_id === agencyId);
      const patch = {
        recipient_agency_id: agencyId,
        recipient_agency_name: ag?.agency_name || null,
        recipient_is_subagency: (ag?.depth || 0) > 1,
        recipient_confirmed: true,
      };
      setLines((prev) => prev.map((l) => {
        if (!ids.includes(l.line_id)) return l;
        const suggested = l.suggested_recipient_agency_id;
        const overridden = !!suggested && suggested !== agencyId;
        return {
          ...l,
          ...patch,
          recipient_manually_overridden: overridden,
          reconciliation_status: overridden
            ? 'manual_recipient'
            : (l.agent_matched ? 'matched' : 'placeholder'),
        };
      }));
      // Flash-highlight the just-updated rows
      setJustUpdatedIds(new Set(ids));
      setTimeout(() => setJustUpdatedIds(new Set()), 1200);
      setSelectedIds(new Set());
      refreshSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk assign failed');
    }
  };

  const toggleSelect = (lineId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === lines.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(lines.map((l) => l.line_id)));
  };

  const filteredAgencies = useMemo(() => {
    const q = bulkSearch.trim().toLowerCase();
    if (!q) return agencies;
    return agencies.filter((a) => (a.agency_name || '').toLowerCase().includes(q));
  }, [agencies, bulkSearch]);

  if (!isReady) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-slate-500">
          Mark this import as Ready to unlock the Reconciliation panel.
        </CardContent>
      </Card>
    );
  }

  // Initial state — no reconciliation run yet
  if (!anyReconciled && !loading && lines.every((l) => !l.reconciliation_status || l.reconciliation_status === 'unreconciled')) {
    return (
      <Card>
        <CardContent className="p-10 text-center" data-testid="start-reconciliation-card">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-slate-100">
            <User className="text-slate-500" />
          </div>
          <div className="text-lg font-semibold text-slate-900">Ready to reconcile</div>
          <div className="mx-auto mt-1 max-w-lg text-sm text-slate-500">
            The engine will match every commission line to an Agent by NPN, resolve the current
            Agency, and suggest a recipient. Amount To Pay stays manual, always.
          </div>
          <Button
            className="mt-5"
            onClick={startReconciliation}
            disabled={reconciling}
            data-testid="run-reconciliation-btn"
          >
            {reconciling
              ? (<><Loader2 size={14} className="mr-1 animate-spin" /> Reconciling…</>)
              : (<>Start Reconciliation <ArrowRight size={14} className="ml-1" /></>)}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="reconciliation-panel">
      {/* Summary strip */}
      <div className="grid grid-cols-6 gap-2">
        <StatChip label="Total"        value={summary?.total || 0} />
        <StatChip label="Matched"      value={summary?.matched || 0}          tone="emerald" />
        <StatChip label="Placeholders" value={summary?.placeholders || 0}     tone="blue" />
        <StatChip label="Manual"       value={summary?.manual_recipient || 0} tone="violet" />
        <StatChip label="Needs Review" value={summary?.needs_review || 0}     tone="amber" />
        <StatChip label="Reconciled"   value={summary?.reconciled_total || 0} tone="slate" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <Badge
              key={f.key}
              variant={filter === f.key ? 'default' : 'outline'}
              className="cursor-pointer text-xs"
              onClick={() => setFilter(f.key)}
              data-testid={`recon-filter-${f.key}`}
            >
              {f.label}
            </Badge>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={startReconciliation} disabled={reconciling}
                  data-testid="recon-refresh-btn">
            {reconciling ? <Loader2 size={12} className="animate-spin" /> : 'Re-run reconciliation'}
          </Button>
          {/* Phase 27.53 — Bulk apply commission % */}
          <div className="flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-1"
               data-testid="bulk-pct-wrap">
            <Percent size={11} className="text-slate-400" />
            <Input
              type="number"
              step="0.01"
              min={0}
              max={100}
              placeholder="Apply %"
              value={bulkPctInput}
              onChange={(e) => setBulkPctInput(e.target.value)}
              className="h-6 w-20 text-xs border-0 focus-visible:ring-0 shadow-none px-1"
              disabled={selectedIds.size === 0}
              data-testid="bulk-pct-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={bulkApplyPct}
              disabled={selectedIds.size === 0 || bulkPctInput === ''}
              className="h-6 px-2 text-xs"
              data-testid="bulk-pct-apply"
              title={selectedIds.size === 0 ? 'Select rows first' : `Apply to ${selectedIds.size} rows`}
            >
              Apply ({selectedIds.size})
            </Button>
          </div>
          <Popover open={bulkPopoverOpen} onOpenChange={setBulkPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                disabled={selectedIds.size === 0}
                data-testid="bulk-assign-btn"
              >
                <Users size={12} className="mr-1" /> Assign recipient ({selectedIds.size})
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2" align="end">
              <div className="relative mb-2">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  autoFocus
                  value={bulkSearch}
                  onChange={(e) => setBulkSearch(e.target.value)}
                  placeholder="Search Agency / Subagency…"
                  className="pl-6 h-8 text-xs"
                  data-testid="bulk-search-input"
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                {filteredAgencies.map((a) => (
                  <button
                    key={a.agency_id}
                    onClick={() => bulkAssign(a.agency_id)}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-xs flex items-center gap-2"
                    data-testid={`bulk-agency-${a.agency_id}`}
                  >
                    <Building2 size={11} className="text-slate-500" />
                    <span className={a.depth > 1 ? 'text-slate-500' : 'font-medium'}>{a.agency_name}</span>
                    {a.depth > 1 && <span className="text-[10px] text-violet-600">Subagency</span>}
                  </button>
                ))}
                {filteredAgencies.length === 0 && (
                  <div className="p-3 text-center text-xs text-slate-400">No matches</div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Lines table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="recon-table">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-[10px] uppercase text-slate-500">
                    <th className="py-2 px-2 w-8">
                      <Checkbox
                        checked={lines.length > 0 && selectedIds.size === lines.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="recon-select-all"
                      />
                    </th>
                    <th className="py-2 px-2">Producer</th>
                    <th className="py-2 px-2">NPN</th>
                    <th className="py-2 px-2">Product</th>
                    <th className="py-2 px-2">Recipient</th>
                    <th className="py-2 px-2 text-right">Amt Received</th>
                    <th className="py-2 px-2 text-right w-20">Comm %</th>
                    <th className="py-2 px-2 text-right">Amt To Pay</th>
                    <th className="py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln) => (
                    <ReconRow
                      key={ln.line_id}
                      line={ln}
                      agencies={agencies}
                      selected={selectedIds.has(ln.line_id)}
                      onToggle={() => toggleSelect(ln.line_id)}
                      onRecipient={(agencyId) => setRecipient(ln.line_id, agencyId)}
                      onCommitAmount={(v) => commitAmount(ln.line_id, v)}
                      onCommitPct={(v) => commitPct(ln.line_id, v)}
                      draft={amountDrafts[ln.line_id]}
                      setDraft={(v) => setAmountDrafts((p) => ({ ...p, [ln.line_id]: v }))}
                      pctDraft={pctDrafts[ln.line_id]}
                      setPctDraft={(v) => setPctDrafts((p) => ({ ...p, [ln.line_id]: v }))}
                      saving={savingLine === ln.line_id}
                      justUpdated={justUpdatedIds.has(ln.line_id)}
                    />
                  ))}
                  {lines.length === 0 && (
                    <tr><td colSpan={9} className="p-8 text-center text-slate-400">No lines match this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────

function StatChip({ label, value, tone = 'slate' }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-800',
    blue: 'bg-blue-50 text-blue-800',
    violet: 'bg-violet-50 text-violet-800',
    amber: 'bg-amber-50 text-amber-800',
    slate: 'bg-slate-50 text-slate-800',
  };
  return (
    <div className={`rounded-md border border-slate-200 ${tones[tone]} px-2.5 py-1.5`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="text-[10px] uppercase opacity-70">{label}</div>
      <div className="text-lg font-semibold leading-none">{value}</div>
    </div>
  );
}

function ReconRow({
  line, agencies, selected, onToggle, onRecipient,
  onCommitAmount, onCommitPct,
  draft, setDraft, pctDraft, setPctDraft,
  saving, justUpdated,
}) {
  const status = line.reconciliation_status || 'unreconciled';
  const style = STATUS_STYLE[status] || STATUS_STYLE.unreconciled;
  const StatusIcon = style.icon;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agencies;
    return agencies.filter((a) => (a.agency_name || '').toLowerCase().includes(q));
  }, [agencies, search]);

  const amountValue = draft !== undefined ? draft : (line.amount_to_pay != null ? String(line.amount_to_pay) : '');
  const pctValue = pctDraft !== undefined
    ? pctDraft
    : (line.commission_pct != null ? String(line.commission_pct) : '');

  return (
    <tr
      className={`border-b border-slate-50 transition-colors duration-700 ${
        justUpdated ? 'bg-emerald-50/70' : 'hover:bg-slate-50/50'
      }`}
      data-testid={`recon-row-${line.line_id}`}
      data-just-updated={justUpdated || undefined}
    >
      <td className="py-1.5 px-2">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          data-testid={`recon-select-${line.line_id}`}
        />
      </td>
      <td className="py-1.5 px-2">
        <div className="flex items-center gap-1.5">
          {line.agent_matched
            ? <User size={11} className="text-emerald-600 shrink-0" />
            : <UserX size={11} className="text-blue-600 shrink-0" />}
          <div>
            <div className="font-medium text-slate-800 leading-tight">
              {line.agent_matched
                ? `${line.agent_first_name || ''} ${line.agent_last_name || ''}`.trim() || line.producer_name || '—'
                : (line.producer_name || <span className="italic text-slate-400">Unknown</span>)}
            </div>
            {!line.agent_matched && line.placeholder_id && (
              <div className="text-[10px] text-blue-600">Accounting placeholder</div>
            )}
          </div>
        </div>
      </td>
      <td className="py-1.5 px-2 font-mono">{line.agent_npn || '—'}</td>
      <td className="py-1.5 px-2 text-slate-600">{line.product || '—'}</td>
      <td className="py-1.5 px-2">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              className="text-left min-w-[160px] rounded border border-slate-200 px-1.5 py-1 text-xs hover:bg-white transition"
              data-testid={`recipient-picker-${line.line_id}`}
              disabled={saving}
            >
              {line.recipient_agency_id ? (
                <div>
                  <div className="font-medium text-slate-800 leading-tight">{line.recipient_agency_name}</div>
                  {line.recipient_is_subagency && (
                    <div className="text-[10px] text-violet-600">Subagency</div>
                  )}
                  {line.recipient_manually_overridden && (
                    <div className="text-[10px] text-violet-600">Manual override</div>
                  )}
                </div>
              ) : (
                <div className="text-slate-400 italic">Pick Agency…</div>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2" align="start">
            <div className="relative mb-2">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Agency / Subagency…"
                className="pl-6 h-8 text-xs"
              />
            </div>
            <div className="max-h-64 overflow-y-auto">
              {line.recipient_agency_id && (
                <button
                  onClick={() => { setPickerOpen(false); onRecipient(null); }}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-xs text-red-600"
                >Clear recipient</button>
              )}
              {filtered.map((a) => (
                <button
                  key={a.agency_id}
                  onClick={() => { setPickerOpen(false); onRecipient(a.agency_id); }}
                  className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-xs flex items-center gap-2 ${
                    a.agency_id === line.recipient_agency_id ? 'bg-slate-100' : ''
                  }`}
                  data-testid={`recipient-option-${a.agency_id}`}
                >
                  <Building2 size={11} className="text-slate-500" />
                  <span className={a.depth > 1 ? 'text-slate-600' : 'font-medium'}>{a.agency_name}</span>
                  {a.depth > 1 && <span className="text-[10px] text-violet-600">Subagency</span>}
                  {a.agency_id === line.suggested_recipient_agency_id && (
                    <span className="ml-auto text-[10px] text-emerald-700">suggested</span>
                  )}
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="p-3 text-center text-xs text-slate-400">No matches</div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </td>
      <td className="py-1.5 px-2 text-right font-mono">
        {line.amount_received_by_kr != null ? `$${Number(line.amount_received_by_kr).toFixed(2)}` : '—'}
      </td>
      <td className="py-1.5 px-2 text-right">
        <div className="relative inline-block">
          <Input
            type="number"
            step="0.01"
            min={0}
            max={100}
            value={pctValue}
            onChange={(e) => setPctDraft(e.target.value)}
            onBlur={(e) => {
              const current = line.commission_pct != null ? String(line.commission_pct) : '';
              if ((e.target.value || '') !== current) {
                onCommitPct(e.target.value);
              }
              setPctDraft(undefined);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            className="h-7 w-16 text-right text-xs font-mono ml-auto pr-4"
            disabled={saving || line.amount_received_by_kr == null}
            data-testid={`pct-input-${line.line_id}`}
            placeholder="—"
          />
          <Percent size={9} className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </td>
      <td className="py-1.5 px-2 text-right">
        <Input
          type="number"
          step="0.01"
          value={amountValue}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => {
            if ((e.target.value || '') !== (line.amount_to_pay != null ? String(line.amount_to_pay) : '')) {
              onCommitAmount(e.target.value);
            }
            setDraft(undefined);
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="h-7 w-24 text-right text-xs font-mono ml-auto"
          disabled={saving}
          data-testid={`amount-input-${line.line_id}`}
          placeholder="—"
        />
      </td>
      <td className="py-1.5 px-2">
        <div className="flex items-center gap-1.5">
          <Badge className={`${style.cls} gap-1 py-0 px-1.5 text-[10px]`}>
            <StatusIcon size={9} /> {style.label}
          </Badge>
          {justUpdated && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 font-medium animate-in fade-in"
              data-testid={`row-updated-flag-${line.line_id}`}
            >
              <CheckCircle2 size={10} /> Saved
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}
